import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Grid,
  TextField,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  HelpCircle,
  Palette,
  Image as ImageIcon,
  Pipette,
  Upload,
  Trash2,
  Languages,
  MessageCircle,
  X as XIcon,
  Check as CheckIconLucide,
} from "lucide-react";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";
import { toast } from "react-toastify";

import useSettings from "../../hooks/useSettings";
import useAuth from "../../hooks/useAuth.js/index.js";
import { i18n } from "../../translate/i18n";
import OnlyForSuperUser from "../OnlyForSuperUser";
import AppTooltip from "../AppTooltip";
import ColorBoxModal from "../ColorBoxModal/index.js";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import CustomToggleSwitch from "../CustomToggleSwitch";

const defaultLogoLight = "/logo-light.png";
const defaultLogoDark = "/logo-dark.png";
const defaultLogoFavicon = "/favicon.png";
const defaultWhatsAppPhone = "5581989974426";

const normalizeLogoValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value.value === "string") return value.value;
  return "";
};

// Catálogo de idiomas suportados — espelha frontend/components/UserLanguageSelector
const ALL_LANGUAGES = [
  { code: "pt-BR", flag: "/flags/br.png", name: "Português" },
  { code: "en", flag: "/flags/us.png", name: "English" },
  { code: "es", flag: "/flags/es.png", name: "Español" },
  { code: "tr", flag: "/flags/tr.png", name: "Türkçe" },
  { code: "ar", flag: "/flags/sa.png", name: "العربية" },
];

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      ...(theme.scrollbarStyles || {}),
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 4px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    card: {
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      padding: 16,
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
    },
    cardHeaderIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 30,
      height: 30,
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    cardHeaderTitle: {
      fontSize: 13.5,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
    },
    cardHeaderSubtitle: {
      fontSize: 11.5,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      marginTop: 1,
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    fieldLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      lineHeight: 1.2,
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: isDark ? "#8696a0" : "#667781",
      cursor: "help",
      opacity: 0.6,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    helpTooltipTitle: {
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 12,
    },
    helpTooltipSteps: {
      margin: 0,
      paddingLeft: 16,
      "& li": { marginBottom: 3 },
      "& li:last-child": { marginBottom: 0 },
    },
    roundedField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
      },
      "& .MuiOutlinedInput-input": {
        padding: "12px 14px",
      },
    },
    colorSwatch: {
      width: 22,
      height: 22,
      borderRadius: 6,
      border: `1px solid ${
        dsColors.border || (isDark ? "#2a3140" : "#d6dae0")
      }`,
      flexShrink: 0,
    },
    pickerBtn: {
      borderRadius: 8,
      padding: 6,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark
          ? "rgba(255,255,255,0.06)"
          : "rgba(15,23,42,0.06)",
        color: theme.palette.primary.main,
      },
    },
    logoCard: {
      borderRadius: 12,
      border: `1px solid ${
        dsColors.borderLight || (isDark ? "#1a1f29" : "#e8e8e8")
      }`,
      background: dsColors.surfaceMuted || (isDark ? "#0d1016" : "#ffffff"),
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    logoPreviewArea: {
      height: 130,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      position: "relative",
    },
    logoImg: {
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
    },
    logoEmpty: {
      fontSize: 11.5,
      color: "rgba(120,120,120,0.7)",
      textAlign: "center",
      padding: "0 12px",
    },
    logoBody: {
      padding: 12,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    logoActions: {
      display: "flex",
      gap: 6,
    },
    btnAction: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 12,
      padding: "8px 10px",
      gap: 6,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      border: "none",
      transition: "all 0.18s ease",
      flex: 1,
      justifyContent: "center",
    },
    btnUpload: {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": {
        background: theme.palette.primary.dark,
      },
    },
    btnRemove: {
      background: isDark ? "rgba(229,57,53,0.12)" : "rgba(229,57,53,0.08)",
      color: "#e53935",
      flex: "0 0 auto",
      width: 36,
      padding: 6,
      "&:hover": {
        background: "rgba(229,57,53,0.16)",
      },
    },
    fileNameHint: {
      fontSize: 11,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    uploadInput: {
      display: "none",
    },
    langFlag: {
      width: 22,
      height: 16,
      objectFit: "cover",
      borderRadius: 3,
      border: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#e0e0e0")}`,
      flexShrink: 0,
    },
    langOptionRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    chipRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 6,
    },
    langChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 4px 4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      border: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#e0e0e0")}`,
    },
    langChipFlag: {
      width: 18,
      height: 13,
      objectFit: "cover",
      borderRadius: 2,
    },
    langChipClose: {
      width: 18,
      height: 18,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      cursor: "pointer",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      background: "transparent",
      border: "none",
      padding: 0,
      transition: "all 0.15s ease",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
        color: "#e53935",
      },
    },
    langChipLocked: {
      opacity: 0.6,
    },
    langChipDefault: {
      background: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)",
      borderColor: isDark ? "rgba(99,102,241,0.35)" : "rgba(99,102,241,0.3)",
      color: theme.palette.primary.main,
    },
    langAutocomplete: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        minHeight: 44,
        padding: "4px 12px !important",
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
      },
      "& .MuiAutocomplete-tag": {
        margin: 2,
      },
      "& .MuiAutocomplete-input": {
        padding: "8px 4px !important",
        fontSize: 13.5,
      },
    },
    langOptionItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
    },
    langOptionName: {
      flex: 1,
      fontSize: 13.5,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
    },
    langOptionBadge: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      padding: "2px 7px",
      borderRadius: 999,
      background: isDark ? "rgba(99,102,241,0.18)" : "rgba(99,102,241,0.12)",
      color: theme.palette.primary.main,
    },
    langOptionCheck: {
      width: 18,
      height: 18,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#06cf9c",
      flexShrink: 0,
    },
    langSummary: {
      fontSize: 13,
      fontWeight: 500,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    },
    langSummaryFlag: {
      width: 22,
      height: 16,
      objectFit: "cover",
      borderRadius: 3,
      border: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#e0e0e0")}`,
    },
    toggleBox: {
      minHeight: 44,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "8px 12px",
      borderRadius: 12,
      background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
      border: `1px solid ${
        dsColors.borderLight || (isDark ? "#1a1f29" : "#e0e0e0")
      }`,
    },
    toggleText: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
    },
    toggleTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    toggleHint: {
      fontSize: 11.5,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      lineHeight: 1.25,
    },
  };
});

const buildHelpContent = (classes, key) => {
  const data = i18n.t(`settings.whitelabel.helps.${key}`, {
    returnObjects: true,
    defaultValue: null,
  });
  if (!data || typeof data === "string") return null;
  const description = data.description || "";
  const steps = Array.isArray(data.steps) ? data.steps : [];
  if (!description && steps.length === 0) return null;
  return (
    <div>
      {description && (
        <div className={classes.helpTooltipTitle}>{description}</div>
      )}
      {steps.length > 0 && (
        <ol className={classes.helpTooltipSteps}>
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
    </div>
  );
};

const HelpIcon = ({ classes, helpKey }) => {
  const content = buildHelpContent(classes, helpKey);
  if (!content) return null;
  return (
    <AppTooltip title={content} enterTouchDelay={0} leaveTouchDelay={6000}>
      <span className={classes.helpIcon}>
        <HelpCircle size={14} strokeWidth={2} />
      </span>
    </AppTooltip>
  );
};

const FieldGroup = ({ classes, label, helpKey, children }) => (
  <div className={classes.fieldGroup}>
    <label className={classes.fieldLabel}>
      {label}
      <HelpIcon classes={classes} helpKey={helpKey} />
    </label>
    {children}
  </div>
);

const LogoCard = ({
  classes,
  label,
  helpKey,
  previewUrl,
  previewBg,
  previewLabel,
  fileName,
  onUpload,
  onRemove,
  inputRef,
  uploadId,
  acceptedTypes = "image/png,image/jpeg,image/svg+xml,image/x-icon,image/webp",
}) => (
  <div className={classes.logoCard}>
    <div
      className={classes.logoPreviewArea}
      style={{ background: previewBg }}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={previewLabel}
          className={classes.logoImg}
        />
      ) : (
        <div className={classes.logoEmpty}>
          {i18n.t("settings.whitelabel.previews.empty")}
        </div>
      )}
    </div>
    <div className={classes.logoBody}>
      <FieldGroup classes={classes} label={label} helpKey={helpKey}>
        <span className={classes.fileNameHint} title={fileName || ""}>
          {fileName || i18n.t("settings.whitelabel.previews.empty")}
        </span>
      </FieldGroup>
      <div className={classes.logoActions}>
        <input
          type="file"
          id={uploadId}
          ref={inputRef}
          accept={acceptedTypes}
          className={classes.uploadInput}
          onChange={onUpload}
        />
        <label
          htmlFor={uploadId}
          className={`${classes.btnAction} ${classes.btnUpload}`}
        >
          <Upload size={14} strokeWidth={2} />
          {fileName
            ? i18n.t("settings.whitelabel.actions.replace")
            : i18n.t("settings.whitelabel.actions.upload")}
        </label>
        {fileName && (
          <button
            type="button"
            onClick={onRemove}
            className={`${classes.btnAction} ${classes.btnRemove}`}
            aria-label={i18n.t("settings.whitelabel.actions.remove")}
          >
            <Trash2 size={14} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  </div>
);

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();
  const theme = useTheme();
  const isDark =
    theme.mode === "dark" || theme.palette?.type === "dark";

  const [settingsLoaded, setSettingsLoaded] = useState({});
  const { getCurrentUserInfo } = useAuth();
  const [currentUser, setCurrentUser] = useState({});

  const { colorMode } = useContext(ColorModeContext);
  const [primaryColorLightModalOpen, setPrimaryColorLightModalOpen] = useState(false);
  const [primaryColorDarkModalOpen, setPrimaryColorDarkModalOpen] = useState(false);
  const [sidebarTextColorLightModalOpen, setSidebarTextColorLightModalOpen] = useState(false);
  const [sidebarTextColorDarkModalOpen, setSidebarTextColorDarkModalOpen] = useState(false);

  const [defaultLanguage, setDefaultLanguage] = useState("pt-BR");
  const [availableLanguages, setAvailableLanguages] = useState(
    ALL_LANGUAGES.map((l) => l.code)
  );

  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoLoginLightInput = useRef(null);
  const logoLoginDarkInput = useRef(null);
  const sidebarIconLightInput = useRef(null);
  const sidebarIconDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const appNameInput = useRef(null);
  const [appName, setAppName] = useState(settingsLoaded.appName || "");

  const { update } = useSettings();

  const updateSettingsLoaded = (key, value) => {
    if (
      key === "primaryColorLight" ||
      key === "primaryColorDark" ||
      key === "sidebarTextColorLight" ||
      key === "sidebarTextColorDark" ||
      key === "appName" ||
      key === "appLogoLight" ||
      key === "appLogoDark" ||
      key === "appLogoLoginLight" ||
      key === "appLogoLoginDark" ||
      key === "appSidebarIconLight" ||
      key === "appSidebarIconDark" ||
      key === "appLogoFavicon"
    ) {
      const normalizedValue = normalizeLogoValue(value) || value;
      if (normalizedValue) {
        localStorage.setItem(key, normalizedValue);
      } else {
        localStorage.removeItem(key);
      }
    }
    setSettingsLoaded((prev) => ({ ...prev, [key]: normalizeLogoValue(value) || value }));
  };

  useEffect(() => {
    getCurrentUserInfo().then((u) => setCurrentUser(u || {}));

    if (Array.isArray(settings) && settings.length) {
      const primaryColorLight = settings.find((s) => s.key === "primaryColorLight")?.value;
      const primaryColorDark = settings.find((s) => s.key === "primaryColorDark")?.value;
      const sidebarTextColorLight = settings.find((s) => s.key === "sidebarTextColorLight")?.value;
      const sidebarTextColorDark = settings.find((s) => s.key === "sidebarTextColorDark")?.value;
      const appLogoLight = settings.find((s) => s.key === "appLogoLight")?.value;
      const appLogoDark = settings.find((s) => s.key === "appLogoDark")?.value;
      const appLogoLoginLight = settings.find((s) => s.key === "appLogoLoginLight")?.value;
      const appLogoLoginDark = settings.find((s) => s.key === "appLogoLoginDark")?.value;
      const appSidebarIconLight = settings.find((s) => s.key === "appSidebarIconLight")?.value;
      const appSidebarIconDark = settings.find((s) => s.key === "appSidebarIconDark")?.value;
      const appLogoFavicon = settings.find((s) => s.key === "appLogoFavicon")?.value;
      const loadedAppName = settings.find((s) => s.key === "appName")?.value;
      const whatsappTrialButtonEnabled = settings.find((s) => s.key === "whatsappTrialButtonEnabled")?.value;
      const whatsappTrialButtonPhone = settings.find((s) => s.key === "whatsappTrialButtonPhone")?.value;

      setAppName(loadedAppName || "");
      setSettingsLoaded((prev) => ({
        ...prev,
        primaryColorLight,
        primaryColorDark,
        sidebarTextColorLight: sidebarTextColorLight || "#ffffff",
        sidebarTextColorDark: sidebarTextColorDark || "#ffffff",
        appLogoLight,
        appLogoDark,
        appLogoLoginLight,
        appLogoLoginDark,
        appSidebarIconLight,
        appSidebarIconDark,
        appLogoFavicon,
        appName: loadedAppName,
        whatsappTrialButtonEnabled: whatsappTrialButtonEnabled || "enabled",
        whatsappTrialButtonPhone: String(
          whatsappTrialButtonPhone || defaultWhatsAppPhone
        ).replace(/\D/g, ""),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // Carrega defaultLanguage e availableLanguages de CompaniesSettings
  useEffect(() => {
    if (!currentUser?.companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: cs } = await api.get(
          `/companySettings/${currentUser.companyId}`
        );
        if (cancelled) return;
        if (cs?.defaultLanguage) setDefaultLanguage(cs.defaultLanguage);
        if (cs?.availableLanguages) {
          setAvailableLanguages(
            cs.availableLanguages.split(",").filter(Boolean)
          );
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.companyId]);

  const updateCompanyLanguageSetting = async (column, data) => {
    await api.put("/companySettings/", { column, data });
  };

  const handleChangeDefaultLanguage = async (newDefault) => {
    const oldDefault = defaultLanguage;
    if (newDefault === oldDefault) return;
    setDefaultLanguage(newDefault);

    // Default sempre fica na lista de disponíveis
    let newAvailable = availableLanguages;
    if (!availableLanguages.includes(newDefault)) {
      newAvailable = [...availableLanguages, newDefault];
      setAvailableLanguages(newAvailable);
      try {
        await updateCompanyLanguageSetting(
          "availableLanguages",
          newAvailable.join(",")
        );
      } catch {}
    }

    try {
      await updateCompanyLanguageSetting("defaultLanguage", newDefault);
      // Migra usuários que estavam no default antigo
      await api.post("/companySettings/migrate-language", {
        oldLanguage: oldDefault,
        newLanguage: newDefault,
      });
      window.dispatchEvent(
        new CustomEvent("chatia:company-languages", {
          detail: {
            defaultLanguage: newDefault,
            availableLanguages: newAvailable,
          },
        })
      );
      toast.success(i18n.t("settings.toasts.operationUpdatedSuccess"));
    } catch {
      setDefaultLanguage(oldDefault);
    }
  };

  const handleChangeAvailableLanguages = async (event) => {
    let next = event.target.value;
    // Garante que o default está sempre disponível
    if (!next.includes(defaultLanguage)) next = [...next, defaultLanguage];
    const previous = availableLanguages;
    setAvailableLanguages(next);
    try {
      await updateCompanyLanguageSetting(
        "availableLanguages",
        next.join(",")
      );
      window.dispatchEvent(
        new CustomEvent("chatia:company-languages", {
          detail: {
            defaultLanguage,
            availableLanguages: next,
          },
        })
      );
      toast.success(i18n.t("settings.toasts.operationUpdatedSuccess"));
    } catch {
      setAvailableLanguages(previous);
    }
  };

  const handleRemoveAvailableLang = async (code) => {
    if (code === defaultLanguage) return;
    const next = availableLanguages.filter((l) => l !== code);
    handleChangeAvailableLanguages({ target: { value: next } });
  };

  const handleSaveSetting = async (key, value) => {
    await update({ key, value });
    updateSettingsLoaded(key, value);
    toast.success(i18n.t("settings.toasts.operationUpdatedSuccess"));
  };

  const logoSettingByMode = {
    Light: {
      key: "appLogoLight",
      setValue: colorMode.setAppLogoLight,
    },
    Dark: {
      key: "appLogoDark",
      setValue: colorMode.setAppLogoDark,
    },
    LoginLight: {
      key: "appLogoLoginLight",
      setValue: colorMode.setAppLogoLoginLight,
    },
    LoginDark: {
      key: "appLogoLoginDark",
      setValue: colorMode.setAppLogoLoginDark,
    },
    SidebarIconLight: {
      key: "appSidebarIconLight",
      setValue: colorMode.setAppSidebarIconLight,
    },
    SidebarIconDark: {
      key: "appSidebarIconDark",
      setValue: colorMode.setAppSidebarIconDark,
    },
    Favicon: {
      key: "appLogoFavicon",
      setValue: colorMode.setAppLogoFavicon,
    },
  };

  const uploadLogo = async (e, mode) => {
    if (!e.target.files?.[0]) return;
    const logoSetting = logoSettingByMode[mode];
    if (!logoSetting) return;

    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("settingKey", logoSetting.key);
    formData.append("file", file);

    try {
      const response = await api.post("/settings-whitelabel/logo", formData);
      const uploadedValue = normalizeLogoValue(response.data) || response.data;
      updateSettingsLoaded(logoSetting.key, uploadedValue);
      logoSetting.setValue(getBackendUrl() + "/public/" + uploadedValue);
    } catch (err) {
      console.error(i18n.t("settings.toasts.imageUploadError"), err);
    }
  };

  const buildPreviewUrl = (uploadedValue, fallback) => {
    const value = normalizeLogoValue(uploadedValue);
    if (!value) return fallback;
    if (value.startsWith("http") || value.startsWith("/")) {
      return value;
    }
    return `${getBackendUrl()}/public/${value}`;
  };

  const lightLogoUrl = buildPreviewUrl(
    settingsLoaded.appLogoLight,
    defaultLogoLight
  );
  const darkLogoUrl = buildPreviewUrl(
    settingsLoaded.appLogoDark,
    defaultLogoDark
  );
  const loginLightLogoUrl = buildPreviewUrl(
    settingsLoaded.appLogoLoginLight,
    defaultLogoLight
  );
  const loginDarkLogoUrl = buildPreviewUrl(
    settingsLoaded.appLogoLoginDark,
    defaultLogoDark
  );
  const sidebarIconLightUrl = buildPreviewUrl(
    settingsLoaded.appSidebarIconLight,
    defaultLogoFavicon
  );
  const sidebarIconDarkUrl = buildPreviewUrl(
    settingsLoaded.appSidebarIconDark,
    defaultLogoFavicon
  );
  const faviconUrl = buildPreviewUrl(
    settingsLoaded.appLogoFavicon,
    defaultLogoFavicon
  );

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Palette size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("settings.tabs.whitelabel", {
                defaultValue: "Whitelabel",
              })}
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("settings.whitelabel.subtitle")}
            </div>
          </div>
        </div>
      </div>

      <OnlyForSuperUser
        user={currentUser}
        yes={() => (
          <>
            {/* Card 1 — Identidade Visual */}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <span className={classes.cardHeaderIcon}>
                  <Palette size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className={classes.cardHeaderTitle}>
                    {i18n.t("settings.whitelabel.sections.identity")}
                  </div>
                  <div className={classes.cardHeaderSubtitle}>
                    {i18n.t("settings.whitelabel.sections.identitySubtitle")}
                  </div>
                </div>
              </div>

              <Grid spacing={2} container>
                <Grid xs={12} sm={6} md={4} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t("settings.whitelabel.primaryColorLight")}
                    helpKey="primaryColorLight"
                  >
                    <TextField
                      id="primary-color-light-field"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.roundedField}
                      value={settingsLoaded.primaryColorLight || ""}
                      onClick={() => setPrimaryColorLightModalOpen(true)}
                      placeholder="#1976d2"
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <span
                              className={classes.colorSwatch}
                              style={{
                                backgroundColor:
                                  settingsLoaded.primaryColorLight ||
                                  "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            className={classes.pickerBtn}
                            aria-label={i18n.t(
                              "settings.whitelabel.actions.pickColor"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPrimaryColorLightModalOpen(true);
                            }}
                          >
                            <Pipette size={16} strokeWidth={1.8} />
                          </IconButton>
                        ),
                      }}
                    />
                  </FieldGroup>
                  <ColorBoxModal
                    open={primaryColorLightModalOpen}
                    handleClose={() => setPrimaryColorLightModalOpen(false)}
                    onChange={(color) => {
                      const hexColor = color.startsWith("#")
                        ? color
                        : `#${color}`;
                      handleSaveSetting("primaryColorLight", hexColor);
                      colorMode.setPrimaryColorLight(hexColor);
                    }}
                    currentColor={settingsLoaded.primaryColorLight}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t("settings.whitelabel.primaryColorDark")}
                    helpKey="primaryColorDark"
                  >
                    <TextField
                      id="primary-color-dark-field"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.roundedField}
                      value={settingsLoaded.primaryColorDark || ""}
                      onClick={() => setPrimaryColorDarkModalOpen(true)}
                      placeholder="#4fc3f7"
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <span
                              className={classes.colorSwatch}
                              style={{
                                backgroundColor:
                                  settingsLoaded.primaryColorDark ||
                                  "transparent",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            className={classes.pickerBtn}
                            aria-label={i18n.t(
                              "settings.whitelabel.actions.pickColor"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPrimaryColorDarkModalOpen(true);
                            }}
                          >
                            <Pipette size={16} strokeWidth={1.8} />
                          </IconButton>
                        ),
                      }}
                    />
                  </FieldGroup>
                  <ColorBoxModal
                    open={primaryColorDarkModalOpen}
                    handleClose={() => setPrimaryColorDarkModalOpen(false)}
                    onChange={(color) => {
                      const hexColor = color.startsWith("#")
                        ? color
                        : `#${color}`;
                      handleSaveSetting("primaryColorDark", hexColor);
                      colorMode.setPrimaryColorDark(hexColor);
                    }}
                    currentColor={settingsLoaded.primaryColorDark}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t("settings.whitelabel.sidebarTextColorLight")}
                    helpKey="sidebarTextColorLight"
                  >
                    <TextField
                      id="sidebar-text-color-light-field"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.roundedField}
                      value={settingsLoaded.sidebarTextColorLight || "#ffffff"}
                      onClick={() => setSidebarTextColorLightModalOpen(true)}
                      placeholder="#ffffff"
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <span
                              className={classes.colorSwatch}
                              style={{
                                backgroundColor:
                                  settingsLoaded.sidebarTextColorLight ||
                                  "#ffffff",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            className={classes.pickerBtn}
                            aria-label={i18n.t(
                              "settings.whitelabel.actions.pickColor"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSidebarTextColorLightModalOpen(true);
                            }}
                          >
                            <Pipette size={16} strokeWidth={1.8} />
                          </IconButton>
                        ),
                      }}
                    />
                  </FieldGroup>
                  <ColorBoxModal
                    open={sidebarTextColorLightModalOpen}
                    handleClose={() => setSidebarTextColorLightModalOpen(false)}
                    onChange={(color) => {
                      const hexColor = color.startsWith("#")
                        ? color
                        : `#${color}`;
                      handleSaveSetting("sidebarTextColorLight", hexColor);
                      colorMode.setSidebarTextColorLight(hexColor);
                    }}
                    currentColor={settingsLoaded.sidebarTextColorLight || "#ffffff"}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t("settings.whitelabel.sidebarTextColorDark")}
                    helpKey="sidebarTextColorDark"
                  >
                    <TextField
                      id="sidebar-text-color-dark-field"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.roundedField}
                      value={settingsLoaded.sidebarTextColorDark || "#ffffff"}
                      onClick={() => setSidebarTextColorDarkModalOpen(true)}
                      placeholder="#ffffff"
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <span
                              className={classes.colorSwatch}
                              style={{
                                backgroundColor:
                                  settingsLoaded.sidebarTextColorDark ||
                                  "#ffffff",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            className={classes.pickerBtn}
                            aria-label={i18n.t(
                              "settings.whitelabel.actions.pickColor"
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSidebarTextColorDarkModalOpen(true);
                            }}
                          >
                            <Pipette size={16} strokeWidth={1.8} />
                          </IconButton>
                        ),
                      }}
                    />
                  </FieldGroup>
                  <ColorBoxModal
                    open={sidebarTextColorDarkModalOpen}
                    handleClose={() => setSidebarTextColorDarkModalOpen(false)}
                    onChange={(color) => {
                      const hexColor = color.startsWith("#")
                        ? color
                        : `#${color}`;
                      handleSaveSetting("sidebarTextColorDark", hexColor);
                      colorMode.setSidebarTextColorDark(hexColor);
                    }}
                    currentColor={settingsLoaded.sidebarTextColorDark || "#ffffff"}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t("settings.whitelabel.systemName")}
                    helpKey="systemName"
                  >
                    <TextField
                      id="appname-field"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.roundedField}
                      name="appName"
                      value={appName}
                      inputRef={appNameInput}
                      placeholder={i18n.t(
                        "settings.whitelabel.systemNamePlaceholder"
                      )}
                      onChange={(e) => setAppName(e.target.value)}
                      onBlur={async () => {
                        await handleSaveSetting("appName", appName);
                        colorMode.setAppName(appName || "Multi100");
                      }}
                    />
                  </FieldGroup>
                </Grid>
              </Grid>
            </div>

            {/* Card 2 — Logos */}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <span className={classes.cardHeaderIcon}>
                  <ImageIcon size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className={classes.cardHeaderTitle}>
                    {i18n.t("settings.whitelabel.sections.logos")}
                  </div>
                  <div className={classes.cardHeaderSubtitle}>
                    {i18n.t("settings.whitelabel.sections.logosSubtitle")}
                  </div>
                </div>
              </div>

              <Grid spacing={2} container>
                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.sidebarLightLogo")}
                    helpKey="lightLogo"
                    previewLabel={i18n.t("settings.whitelabel.previews.light")}
                    previewBg="#ffffff"
                    previewUrl={lightLogoUrl}
                    fileName={settingsLoaded.appLogoLight}
                    inputRef={logoLightInput}
                    uploadId="upload-logo-light-button"
                    onUpload={(e) => uploadLogo(e, "Light")}
                    onRemove={() => {
                      handleSaveSetting("appLogoLight", "");
                      colorMode.setAppLogoLight("");
                    }}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.sidebarDarkLogo")}
                    helpKey="darkLogo"
                    previewLabel={i18n.t("settings.whitelabel.previews.dark")}
                    previewBg="#1f2937"
                    previewUrl={darkLogoUrl}
                    fileName={settingsLoaded.appLogoDark}
                    inputRef={logoDarkInput}
                    uploadId="upload-logo-dark-button"
                    onUpload={(e) => uploadLogo(e, "Dark")}
                    onRemove={() => {
                      handleSaveSetting("appLogoDark", "");
                      colorMode.setAppLogoDark("");
                    }}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.loginLightLogo")}
                    helpKey="loginLightLogo"
                    previewLabel={i18n.t("settings.whitelabel.previews.loginLight")}
                    previewBg="#ffffff"
                    previewUrl={loginLightLogoUrl}
                    fileName={settingsLoaded.appLogoLoginLight}
                    inputRef={logoLoginLightInput}
                    uploadId="upload-logo-login-light-button"
                    onUpload={(e) => uploadLogo(e, "LoginLight")}
                    onRemove={() => {
                      handleSaveSetting("appLogoLoginLight", "");
                      colorMode.setAppLogoLoginLight("");
                    }}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.loginDarkLogo")}
                    helpKey="loginDarkLogo"
                    previewLabel={i18n.t("settings.whitelabel.previews.loginDark")}
                    previewBg="#1f2937"
                    previewUrl={loginDarkLogoUrl}
                    fileName={settingsLoaded.appLogoLoginDark}
                    inputRef={logoLoginDarkInput}
                    uploadId="upload-logo-login-dark-button"
                    onUpload={(e) => uploadLogo(e, "LoginDark")}
                    onRemove={() => {
                      handleSaveSetting("appLogoLoginDark", "");
                      colorMode.setAppLogoLoginDark("");
                    }}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.sidebarIconLight")}
                    helpKey="sidebarIconLight"
                    previewLabel={i18n.t("settings.whitelabel.previews.sidebarIconLight")}
                    previewBg="#ffffff"
                    previewUrl={sidebarIconLightUrl}
                    fileName={settingsLoaded.appSidebarIconLight}
                    inputRef={sidebarIconLightInput}
                    uploadId="upload-sidebar-icon-light-button"
                    acceptedTypes="image/png,image/jpeg,image/svg+xml,image/x-icon,image/webp"
                    onUpload={(e) => uploadLogo(e, "SidebarIconLight")}
                    onRemove={() => {
                      handleSaveSetting("appSidebarIconLight", "");
                      colorMode.setAppSidebarIconLight("");
                    }}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.sidebarIconDark")}
                    helpKey="sidebarIconDark"
                    previewLabel={i18n.t("settings.whitelabel.previews.sidebarIconDark")}
                    previewBg="#1f2937"
                    previewUrl={sidebarIconDarkUrl}
                    fileName={settingsLoaded.appSidebarIconDark}
                    inputRef={sidebarIconDarkInput}
                    uploadId="upload-sidebar-icon-dark-button"
                    acceptedTypes="image/png,image/jpeg,image/svg+xml,image/x-icon,image/webp"
                    onUpload={(e) => uploadLogo(e, "SidebarIconDark")}
                    onRemove={() => {
                      handleSaveSetting("appSidebarIconDark", "");
                      colorMode.setAppSidebarIconDark("");
                    }}
                  />
                </Grid>

                <Grid xs={12} sm={6} md={4} item>
                  <LogoCard
                    classes={classes}
                    label={i18n.t("settings.whitelabel.favicon")}
                    helpKey="favicon"
                    previewLabel={i18n.t(
                      "settings.whitelabel.previews.favicon"
                    )}
                    previewBg={isDark ? "#0d1117" : "#f5f7fa"}
                    previewUrl={faviconUrl}
                    fileName={settingsLoaded.appLogoFavicon}
                    inputRef={logoFaviconInput}
                    uploadId="upload-logo-favicon-button"
                    acceptedTypes="image/png,image/x-icon,image/svg+xml"
                    onUpload={(e) => uploadLogo(e, "Favicon")}
                    onRemove={() => {
                      handleSaveSetting("appLogoFavicon", "");
                      colorMode.setAppLogoFavicon("");
                    }}
                  />
                </Grid>
              </Grid>
            </div>

            {/* Card 3 — WhatsApp */}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <span className={classes.cardHeaderIcon}>
                  <MessageCircle size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className={classes.cardHeaderTitle}>
                    {i18n.t("settings.whitelabel.whatsappButton.title", {
                      defaultValue: "Botão de WhatsApp",
                    })}
                  </div>
                  <div className={classes.cardHeaderSubtitle}>
                    {i18n.t("settings.whitelabel.whatsappButton.subtitle", {
                      defaultValue:
                        "Controle o botão de contato mostrado no cadastro e no modo trial.",
                    })}
                  </div>
                </div>
              </div>

              <Grid spacing={2} container>
                <Grid xs={12} sm={5} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t(
                      "settings.whitelabel.whatsappButton.enabledLabel",
                      { defaultValue: "Exibir botões" }
                    )}
                    helpKey="whatsappTrialButtonEnabled"
                  >
                    <div className={classes.toggleBox}>
                      <div className={classes.toggleText}>
                        <span className={classes.toggleTitle}>
                          {i18n.t(
                            "settings.whitelabel.whatsappButton.enabledTitle",
                            { defaultValue: "Botões ativos" }
                          )}
                        </span>
                        <span className={classes.toggleHint}>
                          {i18n.t(
                            "settings.whitelabel.whatsappButton.enabledHint",
                            {
                              defaultValue:
                                "Quando desligado, remove o botão das telas públicas e do trial.",
                            }
                          )}
                        </span>
                      </div>
                      <CustomToggleSwitch
                        checked={
                          (settingsLoaded.whatsappTrialButtonEnabled ||
                            "enabled") === "enabled"
                        }
                        onChange={(event) =>
                          handleSaveSetting(
                            "whatsappTrialButtonEnabled",
                            event.target.checked ? "enabled" : "disabled"
                          )
                        }
                      />
                    </div>
                  </FieldGroup>
                </Grid>

                <Grid xs={12} sm={7} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t(
                      "settings.whitelabel.whatsappButton.phoneLabel",
                      { defaultValue: "Telefone do WhatsApp" }
                    )}
                    helpKey="whatsappTrialButtonPhone"
                  >
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.roundedField}
                      value={
                        settingsLoaded.whatsappTrialButtonPhone ||
                        defaultWhatsAppPhone
                      }
                      placeholder={defaultWhatsAppPhone}
                      onChange={(event) =>
                        setSettingsLoaded((prev) => ({
                          ...prev,
                          whatsappTrialButtonPhone: event.target.value.replace(
                            /\D/g,
                            ""
                          ),
                        }))
                      }
                      onBlur={(event) =>
                        handleSaveSetting(
                          "whatsappTrialButtonPhone",
                          event.target.value.replace(/\D/g, "") ||
                            defaultWhatsAppPhone
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.currentTarget.blur();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+</InputAdornment>
                        ),
                      }}
                    />
                  </FieldGroup>
                </Grid>
              </Grid>
            </div>

            {/* Card 4 — Idiomas */}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <span className={classes.cardHeaderIcon}>
                  <Languages size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className={classes.cardHeaderTitle}>
                    {i18n.t("settings.whitelabel.languages.title")}
                  </div>
                  <div className={classes.cardHeaderSubtitle}>
                    {i18n.t("settings.whitelabel.languages.subtitle")}
                  </div>
                </div>
              </div>

              <Grid spacing={2} container>
                <Grid xs={12} sm={6} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t(
                      "settings.whitelabel.languages.defaultLabel"
                    )}
                    helpKey="defaultLanguage"
                  >
                    {(() => {
                      const defaultOptions = ALL_LANGUAGES.filter((l) =>
                        availableLanguages.includes(l.code)
                      );
                      const defaultValueObj =
                        defaultOptions.find((l) => l.code === defaultLanguage) ||
                        null;
                      return (
                        <Autocomplete
                          size="small"
                          options={defaultOptions}
                          value={defaultValueObj}
                          onChange={(_, v) =>
                            v && handleChangeDefaultLanguage(v.code)
                          }
                          getOptionLabel={(o) => o?.name || ""}
                          getOptionSelected={(o, v) => o?.code === v?.code}
                          disableClearable
                          className={classes.langAutocomplete}
                          classes={{ paper: dropdownClasses.paper }}
                          renderOption={(option) => (
                            <div className={classes.langOptionItem}>
                              <img
                                src={option.flag}
                                alt={option.name}
                                className={classes.langFlag}
                              />
                              <span className={classes.langOptionName}>
                                {option.name}
                              </span>
                              {option.code === defaultLanguage && (
                                <span className={classes.langOptionCheck}>
                                  <CheckIconLucide size={16} strokeWidth={2.4} />
                                </span>
                              )}
                            </div>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={i18n.t(
                                "settings.whitelabel.languages.defaultLabel"
                              )}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: defaultValueObj ? (
                                  <img
                                    src={defaultValueObj.flag}
                                    alt={defaultValueObj.name}
                                    className={classes.langSummaryFlag}
                                    style={{ marginRight: 6 }}
                                  />
                                ) : null,
                              }}
                            />
                          )}
                        />
                      );
                    })()}
                  </FieldGroup>
                </Grid>

                <Grid xs={12} sm={6} item>
                  <FieldGroup
                    classes={classes}
                    label={i18n.t(
                      "settings.whitelabel.languages.availableLabel"
                    )}
                    helpKey="availableLanguages"
                  >
                    {(() => {
                      const selectedObjs = ALL_LANGUAGES.filter((l) =>
                        availableLanguages.includes(l.code)
                      );
                      const summaryText =
                        selectedObjs.length === 0
                          ? ""
                          : selectedObjs.length === 1
                          ? selectedObjs[0].name
                          : i18n.t(
                              "settings.whitelabel.languages.selectedCount",
                              {
                                count: selectedObjs.length,
                                defaultValue: `${selectedObjs.length} idioma(s) selecionado(s)`,
                              }
                            );
                      return (
                        <Autocomplete
                          multiple
                          size="small"
                          options={ALL_LANGUAGES}
                          value={selectedObjs}
                          onChange={(_, v) => {
                            // Default sempre presente
                            let codes = v.map((l) => l.code);
                            if (!codes.includes(defaultLanguage)) {
                              codes = [...codes, defaultLanguage];
                            }
                            handleChangeAvailableLanguages({
                              target: { value: codes },
                            });
                          }}
                          getOptionLabel={(o) => o.name}
                          getOptionSelected={(o, v) => o?.code === v?.code}
                          getOptionDisabled={(o) => o.code === defaultLanguage}
                          disableCloseOnSelect
                          renderTags={() => null}
                          className={classes.langAutocomplete}
                          classes={{ paper: dropdownClasses.paper }}
                          renderOption={(option, { selected }) => (
                            <div className={classes.langOptionItem}>
                              <img
                                src={option.flag}
                                alt={option.name}
                                className={classes.langFlag}
                              />
                              <span className={classes.langOptionName}>
                                {option.name}
                              </span>
                              {option.code === defaultLanguage && (
                                <span className={classes.langOptionBadge}>
                                  {i18n.t(
                                    "settings.whitelabel.languages.defaultBadge"
                                  )}
                                </span>
                              )}
                              <span className={classes.langOptionCheck}>
                                {selected && (
                                  <CheckIconLucide size={16} strokeWidth={2.4} />
                                )}
                              </span>
                            </div>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                summaryText
                                  ? ""
                                  : i18n.t(
                                      "settings.whitelabel.languages.availableLabel"
                                    )
                              }
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: summaryText ? (
                                  <span className={classes.langSummary}>
                                    {summaryText}
                                  </span>
                                ) : null,
                              }}
                            />
                          )}
                        />
                      );
                    })()}
                  </FieldGroup>

                  <div className={classes.chipRow}>
                    {availableLanguages.map((code) => {
                      const lang = ALL_LANGUAGES.find((l) => l.code === code);
                      if (!lang) return null;
                      const isDefault = code === defaultLanguage;
                      return (
                        <span
                          key={code}
                          className={`${classes.langChip} ${
                            isDefault ? classes.langChipDefault : ""
                          } ${isDefault ? classes.langChipLocked : ""}`}
                        >
                          <img
                            src={lang.flag}
                            alt={lang.name}
                            className={classes.langChipFlag}
                          />
                          {lang.name}
                          {!isDefault && (
                            <button
                              type="button"
                              className={classes.langChipClose}
                              onClick={() => handleRemoveAvailableLang(code)}
                              aria-label={i18n.t(
                                "settings.whitelabel.actions.remove"
                              )}
                            >
                              <XIcon size={12} strokeWidth={2.4} />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </Grid>
              </Grid>
            </div>
          </>
        )}
      />
    </div>
  );
}
